import { ChevronDown, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react'
import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { cn } from '../../lib/utils'
import { BrandMark } from '../Brand'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { ScrollArea, Tooltip, TooltipContent, TooltipTrigger } from '../ui/primitives'
import type { NavItem, NavSection } from './nav-model'

export type { NavItem, NavSection } from './nav-model'

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? 'dev'

export function Sidebar({
  sections,
  open,
  onClose,
  collapsed = false,
  onToggleCollapsed,
}: {
  sections: NavSection[]
  /** Mobile only — on desktop the rail is always present. */
  open: boolean
  onClose: () => void
  /** Desktop only — the drawer is always full width when open on mobile. */
  collapsed?: boolean
  onToggleCollapsed?: () => void
}) {
  // The drawer is never collapsed: on a phone the rail is already hidden, and
  // an icon-only drawer would be a worse target for no gain.
  const rail = collapsed && !open

  return (
    <>
      {/* Scrim behind the drawer on small screens. */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-[2px] lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-sidebar text-sidebar-foreground transition-[transform,width] duration-200 lg:static lg:translate-x-0',
          rail ? 'w-60 lg:w-[3.75rem]' : 'w-60',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div
          className={cn(
            'flex h-12 shrink-0 items-center gap-2 border-b border-sidebar-border px-4',
            rail && 'lg:justify-center lg:px-0',
          )}
        >
          <BrandMark className="size-6" alt={rail ? 'Logic Planet' : ''} />
          <span
            className={cn(
              'text-sm font-semibold tracking-tight text-white',
              rail && 'lg:hidden',
            )}
          >
            Logic Planet
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            className="ml-auto text-sidebar-muted hover:bg-sidebar-accent hover:text-white lg:hidden"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <X />
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <nav className={cn('space-y-4 p-2', rail && 'lg:px-1.5')} aria-label="Main">
            {sections.map((section, index) => (
              <div key={section.heading ?? index}>
                {section.heading && (
                  <p
                    className={cn(
                      'px-2 pb-1 text-xs font-medium uppercase tracking-wider text-sidebar-muted',
                      // A divider stands in for the heading once the words go.
                      rail && 'lg:mx-1 lg:mb-1 lg:h-px lg:overflow-hidden lg:bg-sidebar-border lg:p-0 lg:text-transparent',
                    )}
                  >
                    {section.heading}
                  </p>
                )}
                <ul className="space-y-0.5">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      {item.children ? (
                        <NavGroup item={item} rail={rail} onNavigate={onClose} />
                      ) : (
                        <NavItemLink item={item} rail={rail} onNavigate={onClose} />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </ScrollArea>

        <div className="shrink-0 border-t border-sidebar-border">
          {onToggleCollapsed && (
            <button
              type="button"
              onClick={onToggleCollapsed}
              aria-expanded={!collapsed}
              className={cn(
                'hidden w-full items-center gap-2.5 px-4 py-2 text-xs text-sidebar-muted transition-colors hover:bg-sidebar-accent hover:text-white lg:flex',
                rail && 'lg:justify-center lg:px-0',
              )}
            >
              {rail ? (
                <PanelLeftOpen className="size-4 shrink-0" />
              ) : (
                <>
                  <PanelLeftClose className="size-4 shrink-0" />
                  <span>Collapse</span>
                </>
              )}
              <span className="sr-only">{rail ? 'Expand sidebar' : 'Collapse sidebar'}</span>
            </button>
          )}

          <footer
            className={cn(
              'space-y-1 border-t border-sidebar-border px-4 py-2.5 text-xs text-sidebar-muted',
              rail && 'lg:hidden',
            )}
          >
            <div className="flex items-center justify-between">
              <span>Version</span>
              <span className="numeric">{APP_VERSION}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Licence</span>
              <span>Internal use</span>
            </div>
            <a
              href="mailto:itsupport@logicplanet.com"
              className="block pt-0.5 transition-colors hover:text-white"
            >
              Support
            </a>
          </footer>
        </div>
      </aside>
    </>
  )
}

/** A parent row with children under it.
 *
 * The parent is a disclosure button, not a link — `/agent/compliance` is not a
 * page, and a nav entry that looks clickable and goes nowhere is worse than one
 * that plainly opens.
 *
 * It starts open when the current route is inside it, so arriving at SOC 2 from
 * a link does not leave the nav claiming you are nowhere. On the collapsed rail
 * the children are flattened into the tooltip, because a disclosure inside a
 * 60px rail has nowhere to disclose to.
 */
function NavGroup({
  item,
  rail,
  onNavigate,
}: {
  item: NavItem
  rail: boolean
  onNavigate: () => void
}) {
  const { pathname } = useLocation()
  const children = item.children ?? []
  const containsRoute = children.some((child) => pathname.startsWith(child.to))
  const [open, setOpen] = useState(containsRoute)

  if (rail) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={children[0]?.to ?? item.to}
            onClick={onNavigate}
            aria-label={item.label}
            className={cn(
              'group flex items-center justify-center rounded-md px-0 py-1.5 text-sm transition-colors lg:justify-center',
              containsRoute
                ? 'bg-sidebar-accent font-medium text-white'
                : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-white',
            )}
          >
            <item.icon className="size-4 shrink-0 opacity-80" />
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right">
          {item.label}
          <span className="opacity-60"> · {children.map((child) => child.label).join(', ')}</span>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors',
          containsRoute
            ? 'font-medium text-white'
            : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-white',
        )}
      >
        <item.icon className="size-4 shrink-0 opacity-80" />
        <span className="truncate">{item.label}</span>
        <ChevronDown
          className={cn('ml-auto size-3.5 shrink-0 transition-transform', !open && '-rotate-90')}
          aria-hidden
        />
      </button>

      {open && (
        // Indented under a rule rather than by padding alone: the rule is what
        // makes three children read as one family at a glance.
        <ul className="ml-4 mt-0.5 space-y-0.5 border-l border-sidebar-border pl-2">
          {children.map((child) => (
            <li key={child.to}>
              <NavItemLink item={child} rail={false} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/** One nav row. Collapsed, the label moves into a tooltip rather than vanishing
 *  — an icon-only rail with no accessible name is a guessing game. */
function NavItemLink({
  item,
  rail,
  onNavigate,
}: {
  item: NavItem
  rail: boolean
  onNavigate: () => void
}) {
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      aria-label={rail ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors',
          rail && 'lg:justify-center lg:px-0',
          isActive
            ? 'bg-sidebar-accent font-medium text-white'
            : 'text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-white',
        )
      }
    >
      <item.icon className="size-4 shrink-0 opacity-80" />
      <span className={cn('truncate', rail && 'lg:hidden')}>{item.label}</span>
      {item.planned && (
        <span
          className={cn(
            'ml-auto rounded bg-sidebar-accent px-1 text-xs text-sidebar-muted',
            rail && 'lg:hidden',
          )}
          title="Interface only — no API behind it yet"
        >
          soon
        </span>
      )}
      {item.count !== undefined && item.count > 0 && (
        <Badge
          variant="neutral"
          size="sm"
          className={cn('ml-auto numeric', rail && 'lg:hidden')}
        >
          {item.count}
        </Badge>
      )}
    </NavLink>
  )

  if (!rail) return link

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" className="flex items-center gap-2">
        {item.label}
        {item.planned && <span className="opacity-60">· no API yet</span>}
        {item.count !== undefined && item.count > 0 && (
          <span className="numeric opacity-60">{item.count}</span>
        )}
      </TooltipContent>
    </Tooltip>
  )
}
